// Path: src/components/common/FaceRegisterModal.tsx
// Description: 用户人脸录入组件（含活体检测 + 亮度增强 + 真实录制格式）

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Camera, RefreshCw, Check, AlertCircle, Video, Scan, Sun, SunDim, AlertTriangle } from 'lucide-react';
import { enrollFaceWithMetadata, getFaceStatus, parseFaceError } from '../../lib/api/faceV2';
import type { FaceStatus } from '../../lib/api/faceV2';
import {
  getBestRecordingMimeType,
  getRecordingDebugMeta,
  buildFaceRecordUploadPayload,
  validateRecordingDuration,
  logRecordingDebugInfo,
  parseRecordingError,
  RECORDING_ERRORS,
} from '../../lib/utils/videoRecording';
import type { RecordingMetadata } from '../../lib/utils/videoRecording';

interface FaceRegisterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const MIN_RECORDING_DURATION_MS = 3000;
const DEFAULT_RECORDING_DURATION = 5;

const FaceRegisterModal: React.FC<FaceRegisterModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordedVideo, setRecordedVideo] = useState<Blob | null>(null);
  const [capturedImage, setCapturedImage] = useState<Blob | null>(null);
  const [capturedImageUrl, setCapturedImageUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [faceStatus, setFaceStatus] = useState<FaceStatus | null>(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  
  // 亮度增强相关状态
  const [brightness, setBrightness] = useState(120);
  const [showFlash, setShowFlash] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  
  // 录制格式相关状态
  const [requestedMimeType, setRequestedMimeType] = useState('');
  const [actualMimeType, setActualMimeType] = useState('');
  const [recordingStartTime, setRecordingStartTime] = useState<number>(0);
  const [videoWidth, setVideoWidth] = useState(640);
  const [videoHeight, setVideoHeight] = useState(480);
  
  const cameraStreamRef = useRef<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const flashTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const fetchFaceStatus = useCallback(async () => {
    setIsLoadingStatus(true);
    try {
      const response = await getFaceStatus();
      setFaceStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch face status:', err);
    } finally {
      setIsLoadingStatus(false);
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach(track => track.stop());
      cameraStreamRef.current = null;
    }
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    if (flashTimeoutRef.current) {
      clearTimeout(flashTimeoutRef.current);
      flashTimeoutRef.current = null;
    }
    setCameraActive(false);
    setIsRecording(false);
    setRecordingTime(0);
    setShowFlash(false);
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError('');
    setRecordedVideo(null);
    setCapturedImage(null);
    setCapturedImageUrl(null);
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 480 }
        }
      });
      
      cameraStreamRef.current = stream;
      
      const videoTrack = stream.getVideoTracks()[0];
      const settings = videoTrack.getSettings();
      setVideoWidth(settings.width || 640);
      setVideoHeight(settings.height || 480);
      
      setCameraActive(true);
      
      setTimeout(() => {
        if (videoRef.current && cameraStreamRef.current) {
          videoRef.current.srcObject = cameraStreamRef.current;
          videoRef.current.play().catch(e => {
            console.error('Video play error:', e);
          });
        }
      }, 100);
      
    } catch (err: any) {
      console.error('Camera access error:', err);
      const errorMsg = parseRecordingError(err);
      setCameraError(errorMsg);
      setCameraActive(false);
    }
  }, []);

  const startRecording = useCallback(() => {
    if (!cameraStreamRef.current) return;
    
    // 探测最佳录制格式
    const capability = getBestRecordingMimeType();
    const mimeType = capability.requestedMimeType;
    
    setRequestedMimeType(mimeType);
    
    console.log('[录制能力探测]', {
      支持的格式: capability.supportedMimeTypes,
      选择的格式: mimeType
    });
    
    let mediaRecorder: MediaRecorder;
    
    try {
      if (mimeType && MediaRecorder.isTypeSupported(mimeType)) {
        mediaRecorder = new MediaRecorder(cameraStreamRef.current, { mimeType });
      } else {
        mediaRecorder = new MediaRecorder(cameraStreamRef.current);
      }
    } catch (err) {
      console.error('MediaRecorder creation failed:', err);
      setError(RECORDING_ERRORS.RECORDING_START_FAILED);
      return;
    }
    
    // 记录实际使用的 mimeType
    const actualMime = mediaRecorder.mimeType;
    setActualMimeType(actualMime);
    
    console.log('[实际录制格式]', actualMime);
    
    const chunks: Blob[] = [];
    
    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunks.push(e.data);
      }
    };
    
    mediaRecorder.onstop = () => {
      const blob = new Blob(chunks, { type: actualMime });
      setRecordedVideo(blob);
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
      
      // 输出调试信息
      logRecordingDebugInfo({
        requestedMimeType: mimeType,
        actualMimeType: actualMime,
        blobType: blob.type,
        fileName: `face_record.${actualMime.includes('mp4') ? 'mp4' : 'webm'}`,
        fileSize: blob.size,
        durationMs: Date.now() - recordingStartTime,
      });
    };
    
    mediaRecorder.start();
    mediaRecorderRef.current = mediaRecorder;
    setIsRecording(true);
    setRecordingTime(0);
    setRecordingStartTime(Date.now());
    
    recordingIntervalRef.current = setInterval(() => {
      setRecordingTime(prev => {
        if (prev >= DEFAULT_RECORDING_DURATION - 1) {
          if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
          }
          return DEFAULT_RECORDING_DURATION;
        }
        return prev + 1;
      });
    }, 1000);
  }, [recordingStartTime]);

  const captureImage = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    
    setIsEnhancing(true);
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    if (!context) return;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // 触发屏幕闪光效果
    setShowFlash(true);
    flashTimeoutRef.current = setTimeout(() => {
      setShowFlash(false);
    }, 150);
    
    // 绘制原始图像
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // 应用亮度增强
    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    const factor = brightness / 100;
    
    for (let i = 0; i < data.length; i += 4) {
      data[i] = Math.min(255, data[i] * factor);
      data[i + 1] = Math.min(255, data[i + 1] * factor);
      data[i + 2] = Math.min(255, data[i + 2] * factor);
    }
    
    context.putImageData(imageData, 0, 0);
    
    canvas.toBlob((blob) => {
      if (blob) {
        setCapturedImage(blob);
        const url = URL.createObjectURL(blob);
        setCapturedImageUrl(url);
      }
      setIsEnhancing(false);
    }, 'image/jpeg', 0.9);
  }, [brightness]);

  useEffect(() => {
    if (isOpen) {
      fetchFaceStatus();
      startCamera();
    } else {
      stopCamera();
      setRecordedVideo(null);
      setCapturedImage(null);
      if (capturedImageUrl) {
        URL.revokeObjectURL(capturedImageUrl);
        setCapturedImageUrl(null);
      }
      setError('');
      setSuccess('');
    }
    
    return () => {
      stopCamera();
    };
  }, [isOpen, fetchFaceStatus, startCamera, stopCamera]);

  const handleSubmit = async () => {
    if (!recordedVideo) {
      setError('请先录制视频');
      return;
    }
    
    if (!capturedImage) {
      setError('请先拍摄人脸照片');
      return;
    }
    
    // 校验录制时长
    const durationMs = Date.now() - recordingStartTime;
    const validation = validateRecordingDuration(durationMs, MIN_RECORDING_DURATION_MS);
    
    if (!validation.valid) {
      setError(validation.message);
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    setSuccess('');
    
    try {
      // 构建录制元数据
      const metadata: RecordingMetadata = getRecordingDebugMeta({
        requestedMimeType,
        actualMimeType,
        durationMs,
        videoWidth,
        videoHeight,
        facingMode: 'user',
      });
      
      // 构建上传载荷
      const formData = buildFaceRecordUploadPayload(recordedVideo, capturedImage, metadata);
      
      console.log('[上传元数据]', metadata);
      
      const response = await enrollFaceWithMetadata(formData);
      
      if (response.data.success) {
        setSuccess(`人脸录入成功！质量分数: ${(response.data.quality_score * 100).toFixed(1)}%`);
        await fetchFaceStatus();
        if (onSuccess) {
          setTimeout(() => {
            onSuccess();
          }, 2000);
        }
      } else {
        setError(response.data.message || '录入失败');
      }
    } catch (err: any) {
      const errorMsg = parseFaceError(err);
      setError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    stopCamera();
    setRecordedVideo(null);
    setCapturedImage(null);
    if (capturedImageUrl) {
      URL.revokeObjectURL(capturedImageUrl);
      setCapturedImageUrl(null);
    }
    setError('');
    setSuccess('');
    onClose();
  };

  const handleRetake = () => {
    setRecordedVideo(null);
    setCapturedImage(null);
    if (capturedImageUrl) {
      URL.revokeObjectURL(capturedImageUrl);
      setCapturedImageUrl(null);
    }
    setError('');
    startCamera();
  };

  if (!isOpen) return null;

  // 计算CSS滤镜值
  const filterStyle = {
    filter: `brightness(${brightness}%) contrast(${Math.min(110, brightness * 0.9)}%)`,
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      {/* 屏幕闪光效果 */}
      {showFlash && (
        <div 
          className="fixed inset-0 bg-white z-[60] pointer-events-none animate-flash"
          style={{ 
            animation: 'flash 0.15s ease-out forwards',
            opacity: 0.8
          }}
        />
      )}
      
      <div className="relative w-[420px] bg-white rounded-xl p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X size={18} />
        </button>
        
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Scan size={20} className="text-blue-600" />
          人脸录入
        </h3>
        
        {isLoadingStatus ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw size={24} className="animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            {faceStatus?.has_face && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                <div className="flex items-center gap-2 text-green-700 font-medium">
                  <Check size={16} />
                  您已录入人脸数据
                </div>
                {faceStatus.quality_score && (
                  <p className="text-xs text-green-600 mt-1">
                    质量分数: {(faceStatus.quality_score * 100).toFixed(1)}%
                  </p>
                )}
                {faceStatus.created_at && (
                  <p className="text-xs text-green-600">
                    录入时间: {new Date(faceStatus.created_at).toLocaleString()}
                  </p>
                )}
                <p className="text-xs text-green-600 mt-2">
                  如需修改，请联系管理员
                </p>
              </div>
            )}
            
            {error && (
              <div className="mb-4 bg-red-50 text-red-600 px-3 py-2 rounded text-sm flex items-center gap-2">
                <AlertCircle size={16} />
                {error}
              </div>
            )}
            
            {success && (
              <div className="mb-4 bg-green-50 text-green-600 px-3 py-2 rounded text-sm flex items-center gap-2">
                <Check size={16} />
                {success}
              </div>
            )}
            
            {!faceStatus?.has_face && (
              <>
                {cameraError && (
                  <div className="mb-4 bg-yellow-50 text-yellow-600 px-3 py-2 rounded text-sm">
                    {cameraError}
                  </div>
                )}
                
                {/* 步骤指示器 */}
                <div className="flex items-center justify-center gap-2 text-xs mb-3">
                  <div className={`flex items-center gap-1 ${recordedVideo ? 'text-green-600' : 'text-blue-600'}`}>
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${recordedVideo ? 'bg-green-100' : 'bg-blue-100'}`}>
                      {recordedVideo ? '✓' : '1'}
                    </span>
                    <span>录视频</span>
                  </div>
                  <span className="text-gray-300">→</span>
                  <div className={`flex items-center gap-1 ${capturedImage ? 'text-green-600' : !recordedVideo ? 'text-gray-400' : 'text-blue-600'}`}>
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${capturedImage ? 'bg-green-100' : !recordedVideo ? 'bg-gray-100' : 'bg-blue-100'}`}>
                      {capturedImage ? '✓' : '2'}
                    </span>
                    <span>拍照片</span>
                  </div>
                  <span className="text-gray-300">→</span>
                  <div className={`flex items-center gap-1 ${recordedVideo && capturedImage ? 'text-blue-600' : 'text-gray-400'}`}>
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${recordedVideo && capturedImage ? 'bg-blue-100' : 'bg-gray-100'}`}>
                      3
                    </span>
                    <span>提交</span>
                  </div>
                </div>
                
                {/* 亮度调节控制 */}
                {cameraActive && (
                  <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 text-xs text-amber-700">
                        <Sun size={14} />
                        <span className="font-medium">画面亮度增强</span>
                      </div>
                      <span className="text-xs font-mono text-amber-600">{brightness}%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <SunDim size={14} className="text-amber-400" />
                      <input
                        type="range"
                        min="80"
                        max="180"
                        value={brightness}
                        onChange={(e) => setBrightness(Number(e.target.value))}
                        className="flex-1 h-2 bg-amber-200 rounded-lg appearance-none cursor-pointer accent-amber-500"
                      />
                      <Sun size={14} className="text-amber-500" />
                    </div>
                    <p className="text-[10px] text-amber-600 mt-1">
                      调节亮度可使面部更清晰，建议在光线较暗时调高
                    </p>
                  </div>
                )}
                
                {/* 录制格式提示 */}
                {actualMimeType && (
                  <div className="mb-3 p-2 bg-gray-50 border border-gray-200 rounded text-xs text-gray-600">
                    <span className="font-medium">录制格式：</span>
                    {actualMimeType.includes('mp4') ? (
                      <span className="text-green-600">MP4 (推荐)</span>
                    ) : (
                      <span className="text-amber-600">WebM (系统将自动转换)</span>
                    )}
                  </div>
                )}
                
                <div className="relative aspect-[4/3] bg-gray-900 rounded-lg overflow-hidden border border-gray-200 mb-4">
                  {cameraActive ? (
                    <>
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className="w-full h-full object-cover transform scale-x-[-1]"
                        style={filterStyle}
                      />
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className={`w-40 h-40 border-2 rounded-full ${isRecording ? 'border-red-400 animate-pulse' : 'border-blue-400/50'}`}></div>
                      </div>
                      {isRecording && (
                        <div className="absolute top-2 left-1/2 -translate-x-1/2 flex items-center gap-2 text-xs text-red-400 bg-black/50 px-2 py-1 rounded">
                          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                          录制中: {recordingTime}s / {DEFAULT_RECORDING_DURATION}s
                        </div>
                      )}
                      {recordedVideo && !isRecording && (
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-xs text-green-400 bg-black/50 px-2 py-1 rounded">
                          ✓ 视频已录制 ({recordingTime}s)
                        </div>
                      )}
                      {capturedImage && (
                        <div className="absolute bottom-2 right-2 text-xs text-green-400 bg-black/50 px-2 py-1 rounded">
                          ✓ 照片已拍摄
                        </div>
                      )}
                      {isEnhancing && (
                        <div className="absolute inset-0 bg-white/30 flex items-center justify-center">
                          <RefreshCw size={24} className="animate-spin text-blue-500" />
                        </div>
                      )}
                    </>
                  ) : capturedImageUrl ? (
                    <img 
                      src={capturedImageUrl} 
                      alt="Captured face" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                      <Camera size={48} className="mb-2 opacity-50" />
                      <span className="text-sm">正在启动摄像头...</span>
                    </div>
                  )}
                  <canvas ref={canvasRef} className="hidden" />
                </div>
                
                <div className="space-y-2">
                  {cameraActive && !recordedVideo && (
                    <button
                      onClick={isRecording ? undefined : startRecording}
                      disabled={!cameraActive || isSubmitting || isRecording}
                      className="w-full py-2.5 px-4 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {isRecording ? (
                        <>
                          <RefreshCw size={14} className="animate-spin" />
                          录制中... {recordingTime}s
                        </>
                      ) : (
                        <>
                          <Video size={16} />
                          步骤1: 开始录制视频 ({DEFAULT_RECORDING_DURATION}秒)
                        </>
                      )}
                    </button>
                  )}
                  
                  {cameraActive && recordedVideo && !capturedImage && (
                    <button
                      onClick={captureImage}
                      disabled={!cameraActive || isSubmitting || isRecording || isEnhancing}
                      className="w-full py-2.5 px-4 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {isEnhancing ? (
                        <>
                          <RefreshCw size={14} className="animate-spin" />
                          处理中...
                        </>
                      ) : (
                        <>
                          <Camera size={16} />
                          步骤2: 拍摄人脸照片
                        </>
                      )}
                    </button>
                  )}
                  
                  {recordedVideo && capturedImage && (
                    <>
                      {recordingTime < 3 && (
                        <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700 flex items-center gap-2">
                          <AlertTriangle size={14} />
                          录制时长不足3秒，建议重新录制
                        </div>
                      )}
                      <button
                        onClick={handleRetake}
                        disabled={isSubmitting}
                        className="w-full py-2 px-4 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                      >
                        <RefreshCw size={14} className="inline mr-1" />
                        重新录制
                      </button>
                      <button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="w-full py-2.5 px-4 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {isSubmitting ? (
                          <>
                            <RefreshCw size={14} className="animate-spin" />
                            提交中...
                          </>
                        ) : (
                          <>
                            <Check size={16} />
                            步骤3: 确认录入
                          </>
                        )}
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
      
      {/* 内联CSS动画 */}
      <style>{`
        @keyframes flash {
          0% { opacity: 0.8; }
          100% { opacity: 0; }
        }
      `}</style>
    </div>,
    document.body
  );
};

export default FaceRegisterModal;
